// src/components/sampleorders/EditSampleOrderForm.js
import React, { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { httpsCallable } from "firebase/functions";
import {
  Package,
  Send,
  X,
  User,
  UserCheck,
  FileText,
  Calendar as CalendarIcon,
  ClipboardList,
  Truck,
  Plus,
  Trash2,
  Clock,
  ExternalLink,
  GitBranchPlus,
  ChevronDown,
  Mail,
} from "lucide-react";
import { getSampleOrderStatusLabel } from "../../utils/sampleOrderStatusLabels";
import AttachmentsPanel from "../attachments/AttachmentsPanel";
import SampleOrderCommentsPanel from "./SampleOrderCommentsPanel"; // ✅ NEW
import { createSampleOrderPdfBase64, exportSampleDetails } from "./exportSampleDetails";
import { db, functions } from "../../firebase";
import { collection, addDoc, serverTimestamp, doc, getDoc, getDocs, orderBy, query } from "firebase/firestore";
import { TaskService } from "../tasks/TaskService"; // 🔥 NEW: Import TaskService
import { createNotification, NOTIFICATION_TYPES, notifySampleCommentAdded } from "../../utils/notifications";

const STATUS_PIPELINE = [
  "Pending",
  "Approved",
  "Preparation",
  "Shipped",
  "Delivered",
  "Follow Up",
  "Completed",
];

const CONVERTED_TO_ORDER_STATUS = "Converted to Order";
const ALL_STATUSES = [...STATUS_PIPELINE, CONVERTED_TO_ORDER_STATUS, "Cancelled"];
const DELIVERY_STAFF_GROUP_ID = "grp_delivery_staff";
const WAREHOUSE_STAFF_GROUP_ID = "grp_warehouse_staff";
const PENDING_TASK_MODAL_KEY = "supplyhawk:pendingTaskModalId";
const getTodayDateString = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(
    now.getDate()
  ).padStart(2, "0")}`;
};

const DELIVERY_METHODS = [
  { value: "", label: "Not set" },
  { value: "Hand Delivered", label: "Hand Delivered" },
  { value: "Ground", label: "Ground" },
  { value: "Air", label: "Air" },
  { value: "Courier", label: "Courier" },
];

const NEW_ACCOUNT_VALUE = "__new_account__";

const normalizeRequestItem = (item, fallbackInventory = null) => {
  const productId = item?.productId || "";
  const fallback = productId && fallbackInventory ? fallbackInventory.find((inv) => inv.id === productId) : null;
  return {
    productId,
    quantity: Math.max(1, Number(item?.quantity) || 1),
    productName: item?.productName || fallback?.name || "",
    productCode: item?.productCode || item?.sku || fallback?.sku || null,
  };
};

const buildOrderSummary = (items) => {
  const cleanItems = (Array.isArray(items) ? items : [])
    .map((item) => ({
      productId: item?.productId || null,
      quantity: Math.max(1, Number(item?.quantity) || 1),
      productName: item?.productName || null,
      productCode: item?.productCode || item?.sku || null,
      sku: item?.productCode || item?.sku || null,
    }))
    .filter((item) => item.productId);

  const totalQuantity = cleanItems.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
  const primary = cleanItems[0] || null;
  const nameList = cleanItems.map((item) => item.productName).filter(Boolean);

  let productName = primary?.productName || "Sample item";
  if (cleanItems.length === 2 && nameList.length === 2) {
    productName = `${nameList[0]} + ${nameList[1]}`;
  } else if (cleanItems.length > 2) {
    productName = `${nameList.slice(0, 2).join(", ")} + ${cleanItems.length - 2} more`;
  }

  return {
    items: cleanItems,
    quantity: totalQuantity || 1,
    productId: cleanItems.length === 1 ? primary?.productId || null : null,
    productCode: cleanItems.length === 1 ? primary?.productCode || null : null,
    productName,
    additionalItems: cleanItems.slice(1).map((item) => ({
      productId: item.productId,
      quantity: item.quantity,
    })),
    groupMode: cleanItems.length > 1 ? "grouped" : "single",
  };
};

const EditSampleOrderForm = ({
  request,
  inventory = [],
  companyUsers = [],
  accounts = [],
  onSave,
  onCancel,
  isAdmin = false,
  currentUserId = null,
  currentUserGroupIds = [],
  companyId,
  inline = false,
  embedded = false,
  sampleStatusLabelSettings = {},
}) => {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [followUpTask, setFollowUpTask] = useState(null); // 🔥 NEW: State for follow-up task
  const [splitSaving, setSplitSaving] = useState(false);
  const [splitRows, setSplitRows] = useState([]);
  const [splitNotes, setSplitNotes] = useState("");
  const [splitPanelOpen, setSplitPanelOpen] = useState(false);
  const [showActionsMenu, setShowActionsMenu] = useState(false);
  const [showTimestampsPanel, setShowTimestampsPanel] = useState(false);
  const [loadingStatusEvents, setLoadingStatusEvents] = useState(false);
  const [statusEvents, setStatusEvents] = useState([]);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [emailForm, setEmailForm] = useState({
    mode: "user",
    userId: "",
    customEmail: "",
    customMessage: "",
  });
  const actionsMenuRef = useRef(null);

  useEffect(() => {
    if (!showActionsMenu) return undefined;

    const handlePointerDown = (event) => {
      if (actionsMenuRef.current && !actionsMenuRef.current.contains(event.target)) {
        setShowActionsMenu(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
    };
  }, [showActionsMenu]);

  const [form, setForm] = useState({
    productId: "",
    quantity: 1,
    additionalItems: [],
    requestedDate: "",
    deliveryDate: "",
    status: "Pending",
    notes: "",
    cancellationNotes: "",
    accountId: "",
    accountName: "",
    assignedToId: "",
    deliveryMethod: "",
    trackingNumber: "",
    prepPointOfContactEmail: "",
    orderNumber: "",
    groupMode: "single", // NEW: order grouping
  });

  // new-account inline form
  const [newAccount, setNewAccount] = useState({
    name: "",
    email: "",
    addressStreet: "",
    addressCity: "",
    addressState: "",
    addressZip: "",
  });

  // 🔥 NEW: Load follow-up task when request changes
  useEffect(() => {
    const loadFollowUpTask = async () => {
      if (!companyId || !request?.id) {
        setFollowUpTask(null);
        return;
      }

      try {
        // Try to get task by the followUpTaskId field if it exists
        if (request.followUpTaskId) {
          const taskRef = doc(db, "companies", companyId, "tasks", request.followUpTaskId);
          const taskSnap = await getDoc(taskRef);
          if (taskSnap.exists()) {
            setFollowUpTask({
              id: taskSnap.id,
              ...taskSnap.data()
            });
          } else {
            // If not found by ID, try to find by sampleOrderId
            const tasks = await TaskService.getTasksBySampleOrderId(companyId, request.id);
            if (tasks.length > 0) {
              setFollowUpTask(tasks[0]);
            } else {
              setFollowUpTask(null);
            }
          }
        } else {
          // If no followUpTaskId, try to find by sampleOrderId
          const tasks = await TaskService.getTasksBySampleOrderId(companyId, request.id);
          if (tasks.length > 0) {
            setFollowUpTask(tasks[0]);
          } else {
            setFollowUpTask(null);
          }
        }
      } catch (error) {
        console.error("Error loading follow-up task:", error);
        setFollowUpTask(null);
      }
    };

    loadFollowUpTask();
  }, [companyId, request]);

  // Hydrate local state from selected request
  useEffect(() => {
    if (!request) return;

    // Prefer the new "items" array if present, fall back to legacy fields
    const itemsFromRequest = Array.isArray(request.items) ? request.items : [];

    let primaryProductId = request.productId || "";
    let primaryQuantity = request.quantity || 1;
    let additionalItems = [];

    if (itemsFromRequest.length > 0) {
      const first = itemsFromRequest[0] || {};
      primaryProductId = first.productId || primaryProductId;
      primaryQuantity =
        typeof first.quantity === "number" && first.quantity > 0 ? first.quantity : primaryQuantity;

      additionalItems = itemsFromRequest.slice(1).map((it) => ({
        productId: it.productId || "",
        quantity: typeof it.quantity === "number" && it.quantity > 0 ? it.quantity : 1,
      }));
    } else if (Array.isArray(request.additionalItems)) {
      additionalItems = request.additionalItems.map((it) => ({
        productId: it.productId || "",
        quantity: typeof it.quantity === "number" && it.quantity > 0 ? it.quantity : 1,
      }));
    }

    setForm({
      productId: primaryProductId || "",
      quantity: primaryQuantity || 1,
      additionalItems,
      requestedDate: request.requestedDate || "",
      deliveryDate: request.deliveryDate || "",
      status:
        request.status ||
        (request.convertedToOrder ? CONVERTED_TO_ORDER_STATUS : "Pending"),
      notes: request.notes || "",
      cancellationNotes: request.cancellationNotes || "",
      accountId: request.accountId || "",
      accountName: request.accountName || "",
      assignedToId: request.assignedToId || "",
      deliveryMethod: request.deliveryMethod || "",
      trackingNumber: request.trackingNumber || "",
      prepPointOfContactEmail: request.prepPointOfContactEmail || "",
      orderNumber: request.orderNumber || "",
      groupMode: request.groupMode || "single",
    });

    setNewAccount({
      name: "",
      email: "",
      addressStreet: "",
      addressCity: "",
      addressState: "",
      addressZip: "",
    });

    setSplitNotes("");
    setSplitRows(
      itemsFromRequest.map((item, index) => ({
        id: `${request.id || "request"}-${index}`,
        selected: false,
        ...normalizeRequestItem(item, inventory),
      }))
    );
  }, [request, inventory]);

  useEffect(() => {
    if (!request) return;
    const itemsFromRequest = Array.isArray(request.items) ? request.items : [];
    if (!itemsFromRequest.length) return;

    setSplitRows((prev) =>
      prev.length
        ? prev
        : itemsFromRequest.map((item, index) => ({
            id: `${request.id || "request"}-${index}`,
            selected: false,
            ...normalizeRequestItem(item, inventory),
          }))
    );
  }, [inventory, request]);

  useEffect(() => {
    setSplitPanelOpen(false);
  }, [request?.id]);

  useEffect(() => {
    setShowTimestampsPanel(false);
    setStatusEvents([]);
  }, [request?.id]);

  useEffect(() => {
    if (!showActionsMenu) return undefined;

    const handlePointerDown = (event) => {
      if (actionsMenuRef.current?.contains(event.target)) return;
      setShowActionsMenu(false);
    };

    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [showActionsMenu]);

  useEffect(() => {
    const loadStatusEvents = async () => {
      if (!showTimestampsPanel || !companyId || !request?.id) return;

      setLoadingStatusEvents(true);
      try {
        const eventsRef = collection(db, "companies", companyId, "sampleOrders", request.id, "statusEvents");
        const snapshot = await getDocs(query(eventsRef, orderBy("changedAt", "desc")));
        setStatusEvents(snapshot.docs.map((eventDoc) => ({ id: eventDoc.id, ...eventDoc.data() })));
      } catch (error) {
        console.error("Error loading sample order status timestamps:", error);
        setStatusEvents([]);
      } finally {
        setLoadingStatusEvents(false);
      }
    };

    loadStatusEvents();
  }, [showTimestampsPanel, companyId, request?.id]);

  const handleChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAdditionalItemChange = (index, field, value) => {
    setForm((prev) => {
      const items = [...(prev.additionalItems || [])];
      items[index] = {
        ...(items[index] || { productId: "", quantity: 1 }),
        [field]: value,
      };
      return { ...prev, additionalItems: items };
    });
  };

  const handleAddItemRow = () => {
    setForm((prev) => ({
      ...prev,
      additionalItems: [...(prev.additionalItems || []), { productId: "", quantity: 1 }],
    }));
  };

  const handleRemoveItemRow = (index) => {
    setForm((prev) => {
      const items = [...(prev.additionalItems || [])];
      items.splice(index, 1);
      return { ...prev, additionalItems: items };
    });
  };

  const handleNewAccountChange = (field, value) => {
    setNewAccount((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleExportClick = () => {
    exportSampleDetails({
      request,
      form,
      accounts,
      inventory,
      companyUsers,
    });
  };

  const getStatusKey = (value) =>
    String(value || "")
      .trim()
      .replace(/\s+/g, "_")
      .replace(/[./#[\]$]/g, "_");

  const formatAuditTimestamp = (value) => {
    const date =
      value?.toDate?.() ||
      (value?.seconds ? new Date(value.seconds * 1000) : value ? new Date(value) : null);
    if (!date || Number.isNaN(date.getTime())) return null;
    return date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  const getUserDisplayName = (userId, fallback = "System") => {
    if (!userId) return fallback;
    const matchedUser = (companyUsers || []).find(
      (user) => String(user?.id || user?.uid || "") === String(userId)
    );
    return (
      matchedUser?.fullName ||
      matchedUser?.displayName ||
      matchedUser?.email ||
      fallback
    );
  };

  const internalUsers = useMemo(
    () =>
      (companyUsers || [])
        .filter((user) => ["owner", "admin", "enduser"].includes(String(user?.role || "").trim().toLowerCase()))
        .sort((a, b) =>
          String(a?.fullName || a?.displayName || a?.email || "")
            .toLowerCase()
            .localeCompare(String(b?.fullName || b?.displayName || b?.email || "").toLowerCase())
        ),
    [companyUsers]
  );

  const selectedRecipientUser = useMemo(
    () =>
      internalUsers.find((user) => String(user?.id || user?.uid) === String(emailForm.userId || "")) || null,
    [emailForm.userId, internalUsers]
  );

  const importantStatusFallbacks = useMemo(
    () => ({
      Pending: {
        entered: request?.createdAt || null,
        changedBy: request?.createdBy || null,
      },
      Approved: {
        entered:
          request?.status === "Approved"
            ? request?.statusUpdatedAt || request?.autoApprovedAt || null
            : request?.autoApprovedAt || null,
        changedBy: request?.approvedBy || (request?.status === "Approved" ? request?.statusUpdatedBy : null),
      },
      Delivered: {
        entered: request?.deliveredAt || null,
        changedBy: request?.status === "Delivered" ? request?.statusUpdatedBy : null,
      },
      "Follow Up": {
        entered: request?.status === "Follow Up" ? request?.statusUpdatedAt || null : null,
        changedBy: request?.status === "Follow Up" ? request?.statusUpdatedBy : null,
      },
      Completed: {
        entered:
          request?.followUpCompletedAt ||
          (request?.status === "Completed" ? request?.statusUpdatedAt || null : null),
        changedBy:
          request?.followUpCompletedBy ||
          (request?.status === "Completed" ? request?.statusUpdatedBy : null),
      },
      "Converted to Order": {
        entered:
          request?.convertedToOrder === true || request?.status === CONVERTED_TO_ORDER_STATUS
            ? request?.followUpCompletedAt || request?.statusUpdatedAt || null
            : null,
        changedBy:
          request?.convertedToOrder === true || request?.status === CONVERTED_TO_ORDER_STATUS
            ? request?.followUpCompletedBy || request?.statusUpdatedBy || null
            : null,
      },
      Cancelled: {
        entered: request?.cancelledAt || null,
        changedBy: request?.cancelledBy || null,
      },
    }),
    [request]
  );

  const statusTimelineRows = useMemo(
    () =>
      ALL_STATUSES.map((status) => {
        const statusKey = getStatusKey(status);
        const matchingEvents = (statusEvents || []).filter(
          (event) =>
            String(event?.toStatus || event?.status || "").trim() === status
        );
        const latestEvent = matchingEvents[0] || null;
        const entered =
          request?.statusEnteredAt?.[statusKey] ||
          latestEvent?.changedAt ||
          importantStatusFallbacks?.[status]?.entered ||
          null;
        const exited =
          request?.statusExitedAt?.[statusKey] ||
          null;
        const changedBy =
          request?.statusUpdatedBy?.[statusKey] ||
          request?.statusChangedBy?.[statusKey] ||
          latestEvent?.changedBy ||
          latestEvent?.changedByUid ||
          latestEvent?.actorUserId ||
          importantStatusFallbacks?.[status]?.changedBy ||
          null;

        if (!entered && !exited && !changedBy) return null;

        return {
          status,
          entered,
          exited,
          changedBy,
        };
      }).filter(Boolean),
    [importantStatusFallbacks, request, statusEvents]
  );

  const closeEmailDialog = () => {
    setShowEmailDialog(false);
    setEmailError("");
    setEmailForm({
      mode: "user",
      userId: "",
      customEmail: "",
      customMessage: "",
    });
  };

  const handleSendEmail = async () => {
    const toEmail =
      emailForm.mode === "custom"
        ? String(emailForm.customEmail || "").trim()
        : String(selectedRecipientUser?.email || "").trim();

    if (!toEmail) {
      setEmailError("Choose an internal user or enter a custom email.");
      return;
    }

    try {
      setSendingEmail(true);
      setEmailError("");

      const pdfPayload = createSampleOrderPdfBase64({
        request,
        form,
        accounts,
        inventory,
        companyUsers,
      });

      const sendSampleOrderEmail = httpsCallable(functions, "sendSampleOrderEmail");
      await sendSampleOrderEmail({
        companyId,
        requestId: request?.id,
        toEmail,
        recipientName:
          emailForm.mode === "custom"
            ? ""
            : selectedRecipientUser?.fullName || selectedRecipientUser?.displayName || "",
        customMessage: emailForm.customMessage || "",
        pdfBase64: pdfPayload?.pdfBase64 || "",
        fileName: pdfPayload?.fileName || "",
        summary: pdfPayload?.summary || {},
      });

      const notificationAccountName =
        String(
          request?.accountName ||
            pdfPayload?.summary?.accountName ||
            form?.accountName ||
            "Sample order"
        ).trim() || "Sample order";
      const notificationStatus =
        String(request?.status || pdfPayload?.summary?.status || "Pending").trim() || "Pending";

      await createNotification({
        userId: currentUserId,
        companyId,
        type: NOTIFICATION_TYPES.SAMPLE_ORDER_EMAILED,
        title: "Sample order emailed",
        body: `Email sent for ${notificationAccountName} (${notificationStatus}) to ${toEmail}.`,
        entityType: "sampleOrder",
        entityId: request?.id || null,
        linkPath: "/sample-orders",
        dedupeKey: `SAMPLE_ORDER_EMAILED_${companyId || "none"}_${request?.id || "none"}_${toEmail.toLowerCase()}`,
      });

      closeEmailDialog();
      setShowActionsMenu(false);
    } catch (error) {
      console.error("Error sending sample order email:", error);
      setEmailError(error?.message || "Could not send sample order email.");
    } finally {
      setSendingEmail(false);
    }
  };

  const handleOpenTask = () => {
    if (!followUpTask) return;
    try {
      window.sessionStorage.setItem(PENDING_TASK_MODAL_KEY, followUpTask.id);
    } catch {}
    navigate(`/tasks?taskId=${encodeURIComponent(followUpTask.id)}`, {
      state: { openTaskId: followUpTask.id },
    });
  };

  const requestItems = useMemo(() => {
    if (Array.isArray(request?.items) && request.items.length) {
      return request.items.map((item) => normalizeRequestItem(item, inventory));
    }

    const fallback = [];
    if (request?.productId) {
      fallback.push(
        normalizeRequestItem(
          {
            productId: request.productId,
            quantity: request.quantity || 1,
            productName: request.productName,
            productCode: request.productCode,
          },
          inventory
        )
      );
    }

    (Array.isArray(request?.additionalItems) ? request.additionalItems : []).forEach((item) => {
      fallback.push(normalizeRequestItem(item, inventory));
    });

    return fallback.filter((item) => item.productId);
  }, [inventory, request]);

  const splitSelectedCount = splitRows.filter((row) => row.selected).length;

  const handleSplitRowChange = (rowId, field, value) => {
    setSplitRows((prev) =>
      prev.map((row) =>
        row.id === rowId
          ? {
              ...row,
              [field]:
                field === "quantity"
                  ? Math.max(1, Number(value) || 1)
                  : value,
              ...(field === "productId"
                ? (() => {
                    const inv = inventory.find((item) => item.id === value) || null;
                    return {
                      productName: inv?.name || row.productName || "",
                      productCode: inv?.sku || row.productCode || null,
                    };
                  })()
                : {}),
            }
          : row
      )
    );
  };

  const handleSplitOrder = async () => {
    if (!companyId || !request?.id) return;

    const selected = splitRows
      .filter((row) => row.selected)
      .map((row) => {
        const inv = inventory.find((item) => item.id === row.productId) || null;
        return {
          productId: row.productId,
          quantity: Math.max(1, Number(row.quantity) || 1),
          productName: inv?.name || row.productName || "Sample item",
          productCode: inv?.sku || row.productCode || null,
          sku: inv?.sku || row.productCode || null,
        };
      })
      .filter((row) => row.productId);

    const remaining = splitRows
      .filter((row) => !row.selected)
      .map((row) => {
        const inv = inventory.find((item) => item.id === row.productId) || null;
        return {
          productId: row.productId,
          quantity: Math.max(1, Number(row.quantity) || 1),
          productName: inv?.name || row.productName || "Sample item",
          productCode: inv?.sku || row.productCode || null,
          sku: inv?.sku || row.productCode || null,
        };
      })
      .filter((row) => row.productId);

    if (!selected.length) {
      alert("Choose at least one line item to move into the new order.");
      return;
    }

    if (!remaining.length) {
      alert("Leave at least one line item on the original order.");
      return;
    }

    const selectedSummary = buildOrderSummary(selected);
    const remainingSummary = buildOrderSummary(remaining);

    try {
      setSplitSaving(true);

      const splitNote = splitNotes.trim();
      const splitRef = await addDoc(collection(db, "companies", companyId, "sampleOrders"), {
        companyId,
        accountId: form.accountId || request.accountId || null,
        accountName: form.accountName || request.accountName || null,
        assignedToId: form.assignedToId || request.assignedToId || null,
        assignedToName: request.assignedToName || null,
        requestedDate: form.requestedDate || request.requestedDate || null,
        deliveryDate: form.deliveryDate || request.deliveryDate || null,
        notes: [form.notes || request.notes || "", splitNote].filter(Boolean).join("\n\n"),
        createdBy: currentUserId || null,
        createdByName:
          currentUser?.fullName ||
          currentUser?.displayName ||
          currentUser?.name ||
          currentUser?.email ||
          request.createdByName ||
          null,
        createdByRole:
          (currentUser?.role || request.createdByRole || "admin").toString().toLowerCase(),
        status: "Pending",
        groupIds: Array.isArray(request.groupIds) ? request.groupIds : [],
        kitId: request.kitId || null,
        deliveryMethod: form.deliveryMethod || request.deliveryMethod || "",
        trackingNumber: "",
        prepPointOfContactEmail:
          form.prepPointOfContactEmail || request.prepPointOfContactEmail || "",
        splitFromOrderId: request.id,
        splitRootOrderId: request.splitRootOrderId || request.id,
        splitCreatedBy: currentUserId || null,
        splitCreatedAt: serverTimestamp(),
        stockDeducted: false,
        stockRestored: false,
        updatedAt: serverTimestamp(),
        createdAt: serverTimestamp(),
        ...selectedSummary,
      });

      await onSave(request.id, {
        ...remainingSummary,
        notes: form.notes || request.notes || null,
        splitChildOrderIds: Array.from(
          new Set([...(Array.isArray(request.splitChildOrderIds) ? request.splitChildOrderIds : []), splitRef.id])
        ),
        splitRootOrderId: request.splitRootOrderId || request.id,
        updatedAt: serverTimestamp(),
      });

      setSplitNotes("");
      setSplitRows(
        remaining.map((item, index) => ({
          id: `${request.id || "request"}-remaining-${index}`,
          selected: false,
          ...normalizeRequestItem(item, inventory),
        }))
      );

      alert("Split order created successfully. The new order has its own ID and starts as a separate pending shipment.");
    } catch (error) {
      console.error("[EditSampleOrderForm] Split order failed:", error);
      alert(error?.message || "Could not split this order right now.");
    } finally {
      setSplitSaving(false);
    }
  };

  // --- Status / pipeline permissions & lock rules ---------------------------

  const requestStatus =
    request?.status ||
    (request?.convertedToOrder ? CONVERTED_TO_ORDER_STATUS : "Pending");
  const isCancelledPersisted = requestStatus === "Cancelled";
  const isConvertedStatusForm = form.status === CONVERTED_TO_ORDER_STATUS;

  // For pipeline permission logic (Hand Delivered etc.) we look at the *form* status
  const isPipelineStatusForm = STATUS_PIPELINE.includes(form.status);
  const pipelineStatusForm = isPipelineStatusForm ? form.status : "Pending";

  // For lock rules (Approved+ & Cancelled), we look at the *saved* request status
  const isPipelineStatusRequest = STATUS_PIPELINE.includes(requestStatus);
  const pipelineStatusRequest = isPipelineStatusRequest ? requestStatus : "Pending";

  const approvedIndex = STATUS_PIPELINE.indexOf("Approved");
  const currentIndexRequest = STATUS_PIPELINE.indexOf(pipelineStatusRequest);
  const isApprovedOrBeyond =
    isPipelineStatusRequest && approvedIndex !== -1 && currentIndexRequest >= approvedIndex;
  const deliveredIndex = STATUS_PIPELINE.indexOf("Delivered");
  const isDeliveredOrBeyondPersisted =
    isPipelineStatusRequest && deliveredIndex !== -1 && currentIndexRequest >= deliveredIndex;
  const isConvertedPersisted =
    requestStatus === CONVERTED_TO_ORDER_STATUS || request?.convertedToOrder === true;

  // The form's main fields become read-only once the request has actually been
  // saved as Cancelled. Cancelation Notes & attachments are exceptions.
  const formDisabled = isCancelledPersisted;

  const isKitRequest = Boolean(request?.kitId);
  const isPendingPersisted = pipelineStatusRequest === "Pending" && !isCancelledPersisted;
  const lockedAfterDelivery = isDeliveredOrBeyondPersisted || isConvertedPersisted;

  // Account & items lock once the saved status is Approved or beyond, or Cancelled.
  // Kit-based requests also keep their item list fixed even while pending.
  const accountLocked = isApprovedOrBeyond || isCancelledPersisted || lockedAfterDelivery;
  const itemsLocked = isApprovedOrBeyond || isCancelledPersisted || isKitRequest || lockedAfterDelivery;
  const deliveryLocked = isCancelledPersisted || lockedAfterDelivery;
  const canSplitOrder =
    isAdmin &&
    !!companyId &&
    !isKitRequest &&
    isPendingPersisted &&
    requestItems.length > 1;

  const adminUsers =
    companyUsers?.filter((u) => u.role === "admin" || u.role === "owner") || [];
  const nonAdminUsers =
    companyUsers?.filter((u) => u.role !== "admin" && u.role !== "owner") || [];

  const currentUser = companyUsers?.find((u) => u.id === currentUserId) || null;

  // User is considered an admin for status editing if:
  // - Their role is admin/owner, OR
  // - The isAdmin prop is true (e.g. company-level admin)
  const isRoleAdmin = currentUser && (currentUser.role === "admin" || currentUser.role === "owner");
  const canAdminEditStatus = Boolean(isRoleAdmin || isAdmin);

  const isAssignedToCurrent = !!form.assignedToId && !!currentUserId && form.assignedToId === currentUserId;
  const normalizedCurrentUserGroupIds = Array.isArray(currentUserGroupIds) ? currentUserGroupIds.filter(Boolean) : [];
  const isDeliveryStaffAdmin = normalizedCurrentUserGroupIds.includes(DELIVERY_STAFF_GROUP_ID);
  const isWarehouseStaffAdmin = normalizedCurrentUserGroupIds.includes(WAREHOUSE_STAFF_GROUP_ID);
  const deliveryOnlyMode = canAdminEditStatus && isDeliveryStaffAdmin;
  const nonDeliverySectionClass = deliveryOnlyMode ? "pointer-events-none opacity-60 select-none" : "";

  let statusOptionsForSelect = [];
  let statusSelectDisabled = false;
  let statusRestrictionNote = "";

  if (isCancelledPersisted) {
    statusOptionsForSelect = ALL_STATUSES.includes(form.status) ? [form.status] : [form.status];
    statusSelectDisabled = true;
  } else if (lockedAfterDelivery) {
    statusOptionsForSelect = ALL_STATUSES.includes(form.status) ? [form.status] : [form.status];
    statusSelectDisabled = true;
  } else if (canAdminEditStatus && (isDeliveryStaffAdmin || isWarehouseStaffAdmin)) {
    if (isWarehouseStaffAdmin && pipelineStatusForm === "Approved") {
      statusOptionsForSelect = ["Approved", "Preparation", "Shipped"];
      statusSelectDisabled = false;
      statusRestrictionNote = "Warehouse Staff can only move sample orders from Approved to Preparation or Shipped.";
    } else if (isDeliveryStaffAdmin && pipelineStatusForm === "Shipped") {
      statusOptionsForSelect = ["Shipped", "Delivered"];
      statusSelectDisabled = false;
      statusRestrictionNote = "Delivery Staff can only move sample orders from Shipped to Delivered.";
    } else {
      statusOptionsForSelect = [form.status];
      statusSelectDisabled = true;
      statusRestrictionNote = isWarehouseStaffAdmin
        ? "Warehouse Staff can only move sample orders from Approved to Preparation or Shipped."
        : "Delivery Staff can only move sample orders from Shipped to Delivered.";
    }
  } else if (canAdminEditStatus) {
    statusOptionsForSelect = ALL_STATUSES;
    statusSelectDisabled = false;
  } else {
    if (!isPipelineStatusForm) {
      statusOptionsForSelect = [form.status];
      statusSelectDisabled = true;
    } else if (
      form.deliveryMethod === "Hand Delivered" &&
      isAssignedToCurrent &&
      pipelineStatusForm === "Approved"
    ) {
      statusOptionsForSelect = ["Approved", "Delivered"];
      statusSelectDisabled = false;
      statusRestrictionNote = "For Hand Delivered orders assigned to you, you can move Approved to Delivered.";
    } else {
      statusOptionsForSelect = [form.status];
      statusSelectDisabled = true;
    }
  }

  const handleStatusChange = (nextStatus) => {
    if (statusSelectDisabled) return;
    if (!ALL_STATUSES.includes(nextStatus)) return;
    handleChange("status", nextStatus);
    if (nextStatus === "Delivered" && !String(form.deliveryDate || "").trim()) {
      handleChange("deliveryDate", getTodayDateString());
    }
  };

  // --- Accounts: only active in the dropdown -------------------------------

  const activeAccounts = useMemo(() => accounts.filter((a) => a.active !== false), [accounts]);

  const currentAccount = useMemo(() => {
    const requestAccountId = request?.accountId || "";
    return (
      accounts.find((a) => a.id === form.accountId) ||
      accounts.find((a) => a.id === requestAccountId) ||
      null
    );
  }, [accounts, form.accountId, request?.accountId]);

  const accountOptions = useMemo(() => {
    const list = [...activeAccounts];

    if (currentAccount && currentAccount.active === false && !list.find((a) => a.id === currentAccount.id)) {
      list.push(currentAccount);
    }

    list.sort((a, b) =>
      (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" })
    );
    return list;
  }, [activeAccounts, currentAccount]);

  const handleAccountSelectChange = (value) => {
    if (formDisabled || accountLocked) return;

    if (value === NEW_ACCOUNT_VALUE) {
      handleChange("accountId", NEW_ACCOUNT_VALUE);
      handleChange("accountName", "");
      setNewAccount({
        name: "",
        email: "",
        addressStreet: "",
        addressCity: "",
        addressState: "",
        addressZip: "",
      });
      return;
    }

    handleChange("accountId", value);
    const sel = accounts.find((a) => a.id === value) || null;
    handleChange("accountName", sel?.name || "");
    setNewAccount({
      name: "",
      email: "",
      addressStreet: "",
      addressCity: "",
      addressState: "",
      addressZip: "",
    });
  };

  // show cancelation notes field when status is (current or persisted) Cancelled
  const showCancellationNotesField = form.status === "Cancelled" || requestStatus === "Cancelled";

  // --- Save handler ---------------------------------------------------------

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!request?.id || !onSave) return;

    if (!deliveryOnlyMode && !form.productId) {
      alert("Please choose a product.");
      return;
    }
    if (!form.requestedDate) {
      alert("Requested delivery date is required.");
      return;
    }
    if (form.status === CONVERTED_TO_ORDER_STATUS && !String(form.orderNumber || "").trim()) {
      alert("Please enter an order number when marking this request as Converted to Order.");
      return;
    }
    if (!deliveryOnlyMode && (!form.quantity || Number(form.quantity) <= 0)) {
      alert("Quantity must be at least 1.");
      return;
    }

    // Validate additional items (optional)
    const cleanedAdditionalItems = deliveryOnlyMode
      ? []
      : (form.additionalItems || [])
      .map((it) => ({
        productId: (it.productId || "").trim(),
        quantity: Number(it.quantity) || 0,
      }))
      .filter((it) => it.productId && it.quantity > 0);

    let effectiveAccountId = form.accountId || request.accountId || "";
    let effectiveAccountName = form.accountName || currentAccount?.name || request.accountName || "";

    // If "New Account" is selected, create it in Firestore first
    if (effectiveAccountId === NEW_ACCOUNT_VALUE) {
      const name = newAccount.name.trim();
      const email = newAccount.email.trim();
      const street = newAccount.addressStreet.trim();
      const city = newAccount.addressCity.trim();
      const state = newAccount.addressState.trim();
      const zip = newAccount.addressZip.trim();

      if (!name) {
        alert("Please enter a name for the new account.");
        return;
      }
      if (!street || !city || !state || !zip) {
        alert("Please enter the full address (street, city, state, ZIP) for the new account.");
        return;
      }

      if (!companyId) {
        alert("Missing companyId – cannot create account.");
        return;
      }

      const shippingAddress = `${street}, ${city}, ${state} ${zip}`;

      const newAccountData = {
        active: true,
        name,
        email: email || null,
        contact: name,
        contactName: name,
        contactEmail: email || null,

        address: street,
        addressStreet: street,
        addressStreet2: null,
        addressCity: city,
        addressState: state,
        addressZip: zip,

        city,
        state,
        zip,

        shippingAddress,
        companyId,
        flaggedAs: "None",
        hasSampleOrder: false,
        orderPlaced: false,
        sampleOrdered: false,
        visited: false,
        stage: "Prospect",

        notes: null,

        primaryCustomerUserId: currentUserId || null,
        createdBy: currentUserId || null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      try {
        setSaving(true);
        const colRef = collection(db, "companies", companyId, "accounts");
        const docRef = await addDoc(colRef, newAccountData);
        effectiveAccountId = docRef.id;
        effectiveAccountName = name;
      } catch (err) {
        console.error("[EditSampleOrderForm] Failed to create new account:", err);
        alert("Could not create the new account. Please try again.");
        setSaving(false);
        return;
      }
    }

    const selectedProduct = inventory.find((inv) => inv.id === form.productId);
    const assignedUser = companyUsers.find((u) => u.id === form.assignedToId);
    const selectedAccount = accounts.find((a) => a.id === effectiveAccountId) || null;
    const nextStatus = form.status || request.status || "Pending";
    const effectiveAssignedToId = form.assignedToId || request.assignedToId || null;
    const requiresAssigneeAfterApproval = [
      "Approved",
      "Preparation",
      "Shipped",
      "Delivered",
      "Follow Up",
      "Completed",
      CONVERTED_TO_ORDER_STATUS,
    ].includes(nextStatus);

    if (requiresAssigneeAfterApproval && !effectiveAssignedToId) {
      alert("Assign a representative before moving a sample request into or beyond Approved.");
      return;
    }

    // Build full items array so ALL samples are saved on this order
    const primaryItemQuantity = Number(form.quantity) || 1;
    const primaryItem = {
      productId: form.productId || null,
      productName: selectedProduct?.name || null,
      productCode: selectedProduct?.sku || null,
      quantity: primaryItemQuantity,
    };

    const additionalItemsForItemsArray = cleanedAdditionalItems.map((it) => {
      const inv = inventory.find((inv) => inv.id === it.productId);
      return {
        productId: it.productId,
        productName: inv?.name || null,
        productCode: inv?.sku || null,
        quantity: it.quantity,
      };
    });

    const items = [];
    if (primaryItem.productId) items.push(primaryItem);
    items.push(...additionalItemsForItemsArray);

    const totalQuantity = items.reduce((sum, it) => sum + (Number(it.quantity) || 0), 0);

    const primaryName = selectedProduct?.name || request.productName || null;
    let productNameForOrder = primaryName;

    if (items.length > 1 && primaryName) {
      productNameForOrder = `${primaryName} + ${items.length - 1} more`;
    }

    const updates = deliveryOnlyMode
      ? {
          requestedDate: form.requestedDate || request.requestedDate || null,
          deliveryDate:
            form.status === "Delivered"
              ? form.deliveryDate || request.deliveryDate || getTodayDateString()
              : form.deliveryDate || null,
          status: form.status || request.status || "Pending",
          deliveryMethod: form.deliveryMethod || "",
          trackingNumber: form.trackingNumber || "",
          prepPointOfContactEmail: form.prepPointOfContactEmail || "",
        }
      : {
      productId: form.productId || null,
      productName: productNameForOrder,
      productCode: selectedProduct?.sku || request.productCode || null,
      quantity: totalQuantity || 1,

      // New canonical items array
      items,

      // Keep legacy additionalItems for backwards compatibility
      additionalItems: cleanedAdditionalItems,

      requestedDate: form.requestedDate || null,
      deliveryDate:
        form.status === "Delivered"
          ? form.deliveryDate || request.deliveryDate || getTodayDateString()
          : form.deliveryDate || null,

      accountId: effectiveAccountId || selectedAccount?.id || request.accountId || null,
      accountName: effectiveAccountName || selectedAccount?.name || request.accountName || null,

      notes: form.notes || null,
      cancellationNotes: form.cancellationNotes?.trim() || null,

      assignedToId: effectiveAssignedToId,
      assignedToName: assignedUser?.fullName || assignedUser?.email || null,

      status: form.status || request.status || "Pending",
      convertedToOrder: form.status === CONVERTED_TO_ORDER_STATUS,
      orderNumber:
        form.status === CONVERTED_TO_ORDER_STATUS
          ? String(form.orderNumber || "").trim() || null
          : null,

      deliveryMethod: form.deliveryMethod || "",
      trackingNumber: form.trackingNumber || "",
      prepPointOfContactEmail: form.prepPointOfContactEmail || "",

      // Order grouping mode
      groupMode: form.groupMode || "single",
    };

    try {
      setSaving(true);
      await onSave(request.id, updates);
    } catch (err) {
      console.error("[EditSampleOrderForm] Error saving request:", err);
      alert(err?.message || "Could not save changes to this sample request.");
    } finally {
      setSaving(false);
    }
  };

  // 🛑 AFTER all hooks & logic, we can safely guard on request
  if (!request) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 flex items-center justify-center p-8">
        <div className="text-center max-w-xs">
          <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center mx-auto mb-3">
            <Package className="w-5 h-5 text-gray-400" />
          </div>
          <p className="text-sm font-semibold text-gray-800">Select a sample request to edit</p>
          <p className="text-sm text-gray-500 mt-1">
            Choose any request from the list to open its details here.
          </p>
        </div>
      </div>
    );
  }

  const createdAtDate = request.createdAt?.toDate
    ? request.createdAt.toDate()
    : request.createdAt?.seconds
    ? new Date(request.createdAt.seconds * 1000)
    : null;

  const assignedUser = companyUsers.find((u) => u.id === form.assignedToId);
  const showRequestDetailsReadOnly = lockedAfterDelivery;

  return (
    <div
      className={
        inline
          ? "sample-order-form-shell relative flex w-full flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm"
          : embedded
          ? "relative flex w-full max-w-6xl max-h-[94vh] min-h-0 flex-col overflow-hidden overflow-x-hidden rounded-t-3xl border border-slate-200 bg-white shadow-2xl sm:max-h-[90vh] sm:rounded-2xl"
          : "relative flex w-full max-w-6xl max-h-[94vh] flex-col overflow-hidden overflow-x-hidden rounded-t-3xl border border-slate-200 bg-white shadow-2xl sm:max-h-[90vh] sm:rounded-2xl"
      }
      style={inline ? undefined : { touchAction: "pan-y pinch-zoom" }}
    >
      {showEmailDialog ? (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-lg rounded-xl border border-gray-200 bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
              <div>
                <div className="text-sm font-semibold text-gray-900">Email Sample Order</div>
                <div className="text-xs text-gray-500">
                  Send the exported PDF and order details to a warehouse, shipper, or teammate.
                </div>
              </div>
              <button
                type="button"
                onClick={closeEmailDialog}
                className="rounded-md p-1 text-gray-500 hover:bg-gray-100"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-4 px-4 py-4">
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="radio"
                    name="emailMode"
                    checked={emailForm.mode === "user"}
                    onChange={() => setEmailForm((prev) => ({ ...prev, mode: "user" }))}
                  />
                  Internal user
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="radio"
                    name="emailMode"
                    checked={emailForm.mode === "custom"}
                    onChange={() => setEmailForm((prev) => ({ ...prev, mode: "custom" }))}
                  />
                  Custom email
                </label>
              </div>

              {emailForm.mode === "user" ? (
                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-700">Recipient</label>
                  <select
                    value={emailForm.userId}
                    onChange={(e) => setEmailForm((prev) => ({ ...prev, userId: e.target.value }))}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  >
                    <option value="">Select internal user...</option>
                    {internalUsers.map((user) => {
                      const userId = user.id || user.uid;
                      const label = user.fullName || user.displayName || user.email || userId;
                      return (
                        <option key={userId} value={userId}>
                          {label} {user.email ? `(${user.email})` : ""}
                        </option>
                      );
                    })}
                  </select>
                </div>
              ) : (
                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-700">Email address</label>
                  <input
                    type="email"
                    value={emailForm.customEmail}
                    onChange={(e) => setEmailForm((prev) => ({ ...prev, customEmail: e.target.value }))}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                    placeholder="warehouse@example.com"
                  />
                </div>
              )}

              <div>
                <label className="mb-1 block text-xs font-medium text-gray-700">Message</label>
                <textarea
                  rows={4}
                  value={emailForm.customMessage}
                  onChange={(e) => setEmailForm((prev) => ({ ...prev, customMessage: e.target.value }))}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  placeholder="Add shipping, handling, or prep notes..."
                />
              </div>

              {emailError ? (
                <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  {emailError}
                </div>
              ) : null}
            </div>

            <div className="flex items-center justify-end gap-2 border-t border-gray-200 px-4 py-3">
              <button
                type="button"
                onClick={closeEmailDialog}
                className="rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                disabled={sendingEmail}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSendEmail}
                className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
                disabled={sendingEmail}
              >
                <Send className="h-4 w-4" />
                {sendingEmail ? "Sending..." : "Send Email"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {!embedded ? (
        <div className="flex items-start justify-between gap-3 border-b border-slate-200 bg-slate-50 px-4 py-2.5 sm:px-6 sm:py-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center">
              <Package className="w-4 h-4 text-white" />
            </div>
            <div className="min-w-0">
              <h3 className="font-semibold text-gray-900 text-sm">Edit Sample Request</h3>
              <p className="text-sm text-gray-500 truncate">
                {request.productName || "Untitled product"} · {request.accountName || "No account"}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2">
            {followUpTask && (
              <button
                type="button"
                onClick={handleOpenTask}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 text-sm rounded-md border border-green-300 bg-green-50 text-green-700 hover:bg-green-100"
                title="View follow-up task"
              >
                <ClipboardList className="w-4 h-4" />
                View Follow-up Task
                <ExternalLink className="w-3 h-3" />
              </button>
            )}
            <div className="relative" ref={actionsMenuRef}>
              <button
                type="button"
                onClick={() => setShowActionsMenu((prev) => !prev)}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 text-sm rounded-md border border-gray-300 text-gray-700 hover:bg-gray-100"
              >
                Actions
                <ChevronDown className="w-4 h-4" />
              </button>
              {showActionsMenu ? (
                <div className="absolute right-0 top-full z-20 mt-1 w-44 rounded-lg border border-gray-200 bg-white shadow-lg">
                  <button
                    type="button"
                    onClick={() => {
                      handleExportClick();
                      setShowActionsMenu(false);
                    }}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                  >
                    <FileText className="h-4 w-4" />
                    Export
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowTimestampsPanel((prev) => !prev);
                      setShowActionsMenu(false);
                    }}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                  >
                    <Clock className="h-4 w-4" />
                    Timestamps
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowEmailDialog(true);
                      setShowActionsMenu(false);
                    }}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                  >
                    <Mail className="h-4 w-4" />
                    Email To...
                  </button>
                </div>
              ) : null}
            </div>
            <button
              type="button"
              onClick={onCancel}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 text-sm rounded-md border border-gray-300 text-gray-700 hover:bg-gray-100"
            >
              <X className="w-3 h-3" />
              Close
            </button>
          </div>
        </div>
      ) : null}

      <div
        className={
          inline
            ? "min-h-0 flex-1 overflow-y-auto p-4"
            : embedded
            ? "min-h-0 flex-1 overflow-y-auto overflow-x-hidden p-3 sm:p-4"
            : "min-h-0 flex-1 overflow-y-auto overflow-x-hidden p-3 sm:p-4"
        }
      >
        <form className={`flex flex-col gap-3 text-sm sm:gap-4 ${inline ? "min-h-full" : "min-h-full"}`} onSubmit={handleSubmit}>
          {showTimestampsPanel ? (
            <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
              <div className="mb-3 flex items-center justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold text-slate-900">Status Timestamps</div>
                  <div className="text-xs text-slate-500">
                    Review when this sample request moved through each status and who made the change.
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setShowTimestampsPanel(false)}
                  className="rounded-md border border-slate-200 bg-white px-2.5 py-1 text-xs font-medium text-slate-600 hover:bg-slate-100"
                >
                  Hide
                </button>
              </div>

              <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]">
                <div className="rounded-lg border border-slate-200 bg-white">
                  <div className="border-b border-slate-200 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Status Timeline
                  </div>
                  <div className="divide-y divide-slate-100">
                    {statusTimelineRows.length ? (
                      statusTimelineRows.map((row) => (
                        <div key={row.status} className="grid gap-2 px-3 py-2 text-sm sm:grid-cols-[140px_minmax(0,1fr)]">
                          <div className="font-medium text-slate-700">{row.status}</div>
                          <div className="space-y-1 text-slate-600">
                            {row.entered ? (
                              <div>Entered: {formatAuditTimestamp(row.entered)}</div>
                            ) : null}
                            {row.exited ? (
                              <div>Exited: {formatAuditTimestamp(row.exited)}</div>
                            ) : null}
                            {row.changedBy ? (
                              <div>By: {getUserDisplayName(row.changedBy, row.changedBy)}</div>
                            ) : null}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="px-3 py-3 text-sm text-slate-500">
                        No status timestamp metadata has been recorded yet.
                      </div>
                    )}
                  </div>
                </div>

                <div className="rounded-lg border border-slate-200 bg-white">
                  <div className="border-b border-slate-200 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Status Events
                  </div>
                  <div className="divide-y divide-slate-100">
                    {loadingStatusEvents ? (
                      <div className="px-3 py-3 text-sm text-slate-500">Loading timestamps...</div>
                    ) : statusEvents.length ? (
                      statusEvents.map((event) => (
                        <div key={event.id} className="px-3 py-2 text-sm">
                          <div className="font-medium text-slate-800">
                            {event.toStatus || event.status || "Status update"}
                          </div>
                          <div className="mt-1 text-slate-600">
                            {event.fromStatus ? `From ${event.fromStatus} to ` : ""}
                            {event.toStatus || event.status || "updated"}
                          </div>
                          <div className="mt-1 text-xs text-slate-500">
                            {formatAuditTimestamp(event.changedAt) || "Time unavailable"}
                            {" · "}
                            {getUserDisplayName(
                              event.changedBy || event.changedByUid || event.actorUserId,
                              event.changedByName || "System"
                            )}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="px-3 py-3 text-sm text-slate-500">
                        No historical status events have been recorded for this request yet.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ) : null}

          <div className="space-y-4">
          {/* SECTION: Status */}
          <div className={`space-y-2 ${deliveryOnlyMode ? nonDeliverySectionClass : ""}`}>
            <div className="grid grid-cols-1 gap-2 md:grid-cols-[minmax(0,1fr)_220px]">
              <div className="px-1 py-1">
                <div className="flex items-center gap-1.5">
                  <ClipboardList className="w-4 h-4 text-blue-600" />
                  <span
                    className="text-sm font-semibold text-gray-900 cursor-help"
                    title={statusRestrictionNote || "For Hand Delivered orders assigned to you, you can move Approved to Delivered."}
                  >
                    Status
                  </span>
                </div>
                <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center">
                  {form.status === CONVERTED_TO_ORDER_STATUS ? (
                    <input
                      type="text"
                      className={`w-full sm:max-w-[220px] border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 ${
                        formDisabled
                          ? "bg-gray-50 text-gray-500 border-gray-300 cursor-not-allowed"
                          : "bg-white border-purple-200"
                      }`}
                      value={form.orderNumber || ""}
                      onChange={(e) => handleChange("orderNumber", e.target.value)}
                      disabled={formDisabled}
                      placeholder="Order number"
                    />
                  ) : null}
                  <select
                    className={`w-full sm:max-w-[220px] border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      statusSelectDisabled || formDisabled || deliveryOnlyMode
                        ? "bg-gray-50 text-gray-500 border-gray-300 cursor-not-allowed"
                        : "bg-white border-gray-300"
                    }`}
                    value={form.status}
                    onChange={(e) => handleStatusChange(e.target.value)}
                    disabled={statusSelectDisabled || formDisabled || deliveryOnlyMode}
                  >
                    {statusOptionsForSelect.map((s) => (
                      <option key={s} value={s}>
                        {getSampleOrderStatusLabel(s, sampleStatusLabelSettings)}
                      </option>
                    ))}
                  </select>
                </div>
                {statusRestrictionNote ? (
                  <div className="mt-2 text-xs text-slate-500">{statusRestrictionNote}</div>
                ) : null}
              </div>

              <div className="px-1 py-1">
                <label className="block text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-1">
                  Assigned To
                </label>
                {isAdmin && !deliveryOnlyMode ? (
                  <select
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    value={form.assignedToId || ""}
                    onChange={(e) => handleChange("assignedToId", e.target.value)}
                  >
                    <option value="">Unassigned</option>
                    {companyUsers.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.fullName || user.email}
                      </option>
                    ))}
                  </select>
                ) : request.assignedToName || assignedUser ? (
                  <div className="flex items-center gap-1.5 py-2 text-sm text-blue-700">
                    <UserCheck className="w-4 h-4" />
                    <span>{request.assignedToName || assignedUser?.fullName || assignedUser?.email}</span>
                  </div>
                ) : (
                  <div className="py-2 text-sm text-gray-400">Not assigned yet</div>
                )}
              </div>
            </div>

            {!isPipelineStatusForm && !isCancelledPersisted && !isConvertedStatusForm && (
              <p className="text-sm text-amber-700">
                This status ({form.status}) is outside the main pipeline and can&apos;t be changed here.
              </p>
            )}

            {isCancelledPersisted && (
              <p className="text-sm text-red-600">
                This request is <span className="font-semibold">Cancelled</span> and can no longer be edited
                (except cancellation notes & attachments).
              </p>
            )}

            {/* 🔥 NEW: Status change notification for Delivered */}
            {form.status === "Delivered" && request.status !== "Delivered" && (
              <div className="p-2 bg-blue-50 border border-blue-200 rounded">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-blue-600" />
                  <p className="text-sm text-blue-700">
                    <span className="font-medium">Note:</span> When you save with status "Delivered", a follow-up task will be automatically created with a 7-day due date.
                  </p>
                </div>
              </div>
            )}

          </div>

          {/* SECTION: Request Details */}
          <div className={`space-y-3 ${nonDeliverySectionClass}`}>
            <div className="flex items-center gap-2 pb-1 border-b border-gray-200">
              <Package className="w-4 h-4 text-blue-600" />
              <h4 className="text-sm font-semibold text-gray-900">Request Details</h4>
            </div>

            {showRequestDetailsReadOnly ? (
              <div className="px-1 py-1">
                <div>
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Items</div>
                  <div className="mt-1 space-y-1 text-sm text-gray-800">
                    {requestItems.map((item, index) => (
                      <div key={`${item.productId || item.productName || "item"}-${index}`} className="flex items-center justify-between gap-3">
                        <span className="truncate">{item.productName || "Sample item"}</span>
                        <span className="shrink-0 text-gray-500">Qty {item.quantity || 1}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
            <>
            <div className="space-y-2">
              {/* Primary item row */}
              <div className="grid grid-cols-[minmax(0,1fr)_92px] sm:grid-cols-[minmax(0,1fr)_104px] gap-2 items-end">
                <div className="min-w-0">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Product</label>
                  <select
                    className={`w-full border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      itemsLocked || formDisabled
                        ? "bg-gray-50 text-gray-500 border-gray-300 cursor-not-allowed"
                        : "bg-white border-gray-300"
                    }`}
                    value={form.productId}
                    onChange={(e) => handleChange("productId", e.target.value)}
                    required
                    disabled={itemsLocked || formDisabled}
                  >
                    <option value="">Select product</option>
                    {inventory.map((inv) => (
                      <option key={inv.id} value={inv.id}>
                        {inv.name} {inv.sku && `(${inv.sku})`}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Quantity</label>
                  <input
                    type="number"
                    min="1"
                    className={`w-full border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      itemsLocked || formDisabled
                        ? "bg-gray-50 text-gray-500 border-gray-300 cursor-not-allowed"
                        : "bg-white border-gray-300"
                    }`}
                    value={form.quantity}
                    onChange={(e) => handleChange("quantity", e.target.value)}
                    disabled={itemsLocked || formDisabled}
                  />
                </div>
              </div>

              {/* Additional items list */}
              {form.additionalItems?.length > 0 && (
                <div className="space-y-2">
                  {form.additionalItems.map((item, idx) => (
                    <div key={idx} className="grid grid-cols-1 md:grid-cols-3 gap-2 items-end">
                      <div className="md:col-span-2">
                        <select
                          className={`w-full border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                            itemsLocked || formDisabled
                              ? "bg-gray-50 text-gray-500 border-gray-300 cursor-not-allowed"
                              : "bg-white border-gray-300"
                          }`}
                          value={item.productId}
                          onChange={(e) => handleAdditionalItemChange(idx, "productId", e.target.value)}
                          disabled={itemsLocked || formDisabled}
                        >
                          <option value="">Select product</option>
                          {inventory.map((inv) => (
                            <option key={inv.id} value={inv.id}>
                              {inv.name} {inv.sku && `(${inv.sku})`}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="flex gap-2">
                        <div className="flex-1">
  
                          <input
                            type="number"
                            min="1"
                            className={`w-full border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                              itemsLocked || formDisabled
                                ? "bg-gray-50 text-gray-500 border-gray-300 cursor-not-allowed"
                                : "bg-white border-gray-300"
                            }`}
                            value={item.quantity}
                            onChange={(e) => handleAdditionalItemChange(idx, "quantity", e.target.value)}
                            disabled={itemsLocked || formDisabled}
                          />
                        </div>
                        {!itemsLocked && !formDisabled && (
                          <button
                            type="button"
                            onClick={() => handleRemoveItemRow(idx)}
                            className="self-center p-2 rounded-md border border-gray-200 hover:bg-red-50 text-red-600"
                            title="Remove item"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Add item button */}
              {!itemsLocked && !formDisabled && (
                <button
                  type="button"
                  onClick={handleAddItemRow}
                  className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700"
                >
                  <Plus className="w-4 h-4" />
                  Add another item to this shipment
                </button>
              )}
            </div>

            {/* Account + Assigned (same line) */}
            <div className="space-y-2">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {/* Account / Customer : [control/value] */}
                <div className="flex items-center gap-2">
                  <span className="font-medium text-sm text-gray-700 whitespace-nowrap">Account:</span>
                  {isAdmin && !accountLocked && !formDisabled ? (
                    <select
                      className="flex-1 border border-gray-300 rounded px-3 py-2 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      value={form.accountId || ""}
                      onChange={(e) => handleAccountSelectChange(e.target.value)}
                    >
                      <option value="">Select account</option>
                      {accountOptions.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.name || "Unnamed account"}
                        </option>
                      ))}
                      <option value={NEW_ACCOUNT_VALUE}>+ New Account</option>
                    </select>
                  ) : (
                    <div className="flex-1 border border-gray-200 rounded px-3 py-2 bg-gray-50 text-sm text-gray-900">
                      {form.accountName ||
                        currentAccount?.name ||
                        request.accountName ||
                        "No account name set"}
                    </div>
                  )}
                </div>

              </div>

              {/* New Account inline form */}
              {isAdmin && !accountLocked && !formDisabled && form.accountId === NEW_ACCOUNT_VALUE && (
                <div className="mt-1 border border-gray-200 rounded-md bg-gray-50 p-3 space-y-2">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Account Name</label>
                      <input
                        type="text"
                        className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white"
                        value={newAccount.name}
                        onChange={(e) => handleNewAccountChange("name", e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Account Email</label>
                      <input
                        type="email"
                        className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white"
                        value={newAccount.email}
                        onChange={(e) => handleNewAccountChange("email", e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Street Address</label>
                      <input
                        type="text"
                        className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white"
                        value={newAccount.addressStreet}
                        onChange={(e) => handleNewAccountChange("addressStreet", e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                      <input
                        type="text"
                        className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white"
                        value={newAccount.addressCity}
                        onChange={(e) => handleNewAccountChange("addressCity", e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                      <input
                        type="text"
                        className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white"
                        value={newAccount.addressState}
                        onChange={(e) => handleNewAccountChange("addressState", e.target.value.toUpperCase())}
                        maxLength={2}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">ZIP</label>
                      <input
                        type="text"
                        className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white"
                        value={newAccount.addressZip}
                        onChange={(e) => handleNewAccountChange("addressZip", e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
            </>
            )}
          </div>

          {/* SECTION: Delivery */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 pb-1 border-b border-gray-200">
              <Truck className="w-4 h-4 text-blue-600" />
              <h4 className="text-sm font-semibold text-gray-900">Delivery</h4>
            </div>

            {deliveryOnlyMode ? (
              <div className="rounded-lg border border-purple-200 bg-purple-50 px-3 py-2">
                <label className="block text-sm font-medium text-purple-800 mb-1">Delivery Status</label>
                <select
                  className={`w-full md:max-w-[220px] border rounded-md px-3 py-2 text-sm focus:ring-1 focus:ring-purple-500 focus:border-purple-500 ${
                    statusSelectDisabled ? "bg-gray-50 text-gray-500 border-gray-300 cursor-not-allowed" : "bg-white border-purple-200"
                  }`}
                  value={form.status}
                  onChange={(e) => handleStatusChange(e.target.value)}
                  disabled={statusSelectDisabled}
                >
                  {statusOptionsForSelect.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
                {statusRestrictionNote ? (
                  <div className="mt-2 text-xs text-purple-700">{statusRestrictionNote}</div>
                ) : null}
              </div>
            ) : null}

            {/* Requested + Actual Delivery Dates (same line, purple admin style) */}
            <div className="space-y-2">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-purple-800 whitespace-nowrap">
                    Requested Delivery:
                  </span>
                  <input
                    type="date"
                    className={`flex-1 border rounded-md px-3 py-2 text-sm focus:ring-1 focus:ring-purple-500 focus:border-purple-500 bg-purple-50 border-purple-200 ${
                      deliveryLocked ? "cursor-not-allowed opacity-70" : ""
                    }`}
                    value={form.requestedDate || ""}
                    onChange={(e) => handleChange("requestedDate", e.target.value)}
                    disabled={deliveryLocked}
                  />
                </div>

                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-purple-800 whitespace-nowrap">
                    Actual Delivery:
                  </span>
                  <input
                    type="date"
                    className={`flex-1 border rounded-md px-3 py-2 text-sm focus:ring-1 focus:ring-purple-500 focus:border-purple-500 bg-purple-50 border-purple-200 ${
                      deliveryLocked ? "cursor-not-allowed opacity-70" : ""
                    }`}
                    value={form.deliveryDate || ""}
                    onChange={(e) => handleChange("deliveryDate", e.target.value)}
                    disabled={deliveryLocked}
                  />
                </div>
              </div>
            </div>

            {/* Method / Tracking / Prep Contact - lavender admin fields */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <div className="flex items-center gap-2">
                <label className="shrink-0 text-sm font-medium text-purple-800 whitespace-nowrap">Delivery Method</label>
                <select
                  className={`flex-1 min-w-0 border rounded-md px-3 py-2 text-sm focus:ring-1 focus:ring-purple-500 focus:border-purple-500 bg-purple-50 border-purple-200 ${
                    !(isAdmin || deliveryOnlyMode) || deliveryLocked ? "cursor-not-allowed opacity-70" : ""
                  }`}
                  value={form.deliveryMethod || ""}
                  onChange={(e) => handleChange("deliveryMethod", e.target.value)}
                  disabled={!(isAdmin || deliveryOnlyMode) || deliveryLocked}
                >
                  {DELIVERY_METHODS.map((m) => (
                    <option key={m.value} value={m.value}>
                      {m.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-center gap-2">
                <label className="shrink-0 text-sm font-medium text-purple-800 whitespace-nowrap">Tracking Number</label>
                <input
                  type="text"
                  className={`flex-1 min-w-0 border rounded-md px-3 py-2 text-sm focus:ring-1 focus:ring-purple-500 focus:border-purple-500 bg-purple-50 border-purple-200 ${
                    !(isAdmin || deliveryOnlyMode) || deliveryLocked ? "cursor-not-allowed opacity-70" : ""
                  }`}
                  value={form.trackingNumber || ""}
                  onChange={(e) => handleChange("trackingNumber", e.target.value)}
                  disabled={!(isAdmin || deliveryOnlyMode) || deliveryLocked}
                  placeholder="e.g. FedEx / UPS tracking"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-purple-800 mb-1">Prep Contact</label>
                <select
                  className={`w-full border rounded-md px-3 py-2 text-sm focus:ring-1 focus:ring-purple-500 focus:border-purple-500 bg-purple-50 border-purple-200 ${
                    !(isAdmin || deliveryOnlyMode) || deliveryLocked ? "cursor-not-allowed opacity-70" : ""
                  }`}
                  value={form.prepPointOfContactEmail || ""}
                  onChange={(e) => handleChange("prepPointOfContactEmail", e.target.value)}
                  disabled={!(isAdmin || deliveryOnlyMode) || deliveryLocked}
                >
                  <option value="">No prep contact</option>
                  {adminUsers.length > 0 && (
                    <optgroup label="Admins">
                      {adminUsers.map((user) => (
                        <option key={user.id} value={user.email}>
                          {user.fullName || user.email} – {user.email}
                        </option>
                      ))}
                    </optgroup>
                  )}
                  {nonAdminUsers.length > 0 && (
                    <optgroup label="Team members">
                      {nonAdminUsers.map((user) => (
                        <option key={user.id} value={user.email}>
                          {user.fullName || user.email} – {user.email}
                        </option>
                      ))}
                    </optgroup>
                  )}
                </select>
              </div>
            </div>

            {isAdmin && canSplitOrder ? (
              <div className="space-y-2">
                <label className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50/70 px-3 py-2 text-sm text-amber-900">
                  <input
                    type="checkbox"
                    checked={splitPanelOpen}
                    onChange={(e) => setSplitPanelOpen(e.target.checked)}
                    className="h-4 w-4 rounded border-slate-300 text-amber-600 focus:ring-amber-500"
                  />
                  <span className="font-medium">Split</span>
                </label>

                {splitPanelOpen ? (
                  <div className="space-y-2 rounded-lg border border-amber-200 bg-amber-50/70 p-3">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <div className="text-sm font-semibold text-amber-900">Choose items to move</div>
                      </div>
                      <div className="text-xs font-medium text-amber-800">{splitSelectedCount} selected</div>
                    </div>

                    <div className="space-y-2">
                      {splitRows.map((row) => (
                        <label
                          key={row.id}
                          className="grid cursor-pointer grid-cols-1 gap-2 rounded-lg border border-amber-100 bg-white px-3 py-2 md:grid-cols-[auto_minmax(0,1fr)_120px]"
                        >
                          <div className="flex items-center gap-2 text-sm text-slate-700">
                            <input
                              type="checkbox"
                              checked={row.selected}
                              onChange={(e) => handleSplitRowChange(row.id, "selected", e.target.checked)}
                            />
                            <span>Move</span>
                          </div>

                          <select
                            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                            value={row.productId}
                            onChange={(e) => handleSplitRowChange(row.id, "productId", e.target.value)}
                          >
                            <option value="">Select product</option>
                            {inventory.map((inv) => (
                              <option key={inv.id} value={inv.id}>
                                {inv.name} {inv.sku ? `(${inv.sku})` : ""}
                              </option>
                            ))}
                          </select>

                          <input
                            type="number"
                            min="1"
                            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                            value={row.quantity}
                            onChange={(e) => handleSplitRowChange(row.id, "quantity", e.target.value)}
                          />
                        </label>
                      ))}
                    </div>

                    <div className="space-y-1">
                      <label className="block text-xs font-semibold uppercase tracking-wide text-amber-900">
                        Notes for the new order
                      </label>
                      <textarea
                        rows={2}
                        value={splitNotes}
                        onChange={(e) => setSplitNotes(e.target.value)}
                        placeholder="Optional note for the new shipment run..."
                        className="w-full rounded-md border border-amber-200 bg-white px-3 py-2 text-sm"
                      />
                    </div>

                    <div className="flex justify-end">
                      <button
                        type="button"
                        onClick={handleSplitOrder}
                        disabled={splitSaving}
                        className="inline-flex items-center gap-2 rounded-md bg-amber-600 px-3 py-2 text-sm font-semibold text-white hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <GitBranchPlus className="w-4 h-4" />
                        {splitSaving ? "Creating new sample order…" : "Create new sample order with selected items"}
                      </button>
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>

          {/* SECTION: Notes & Files */}
          <div className={`space-y-3 ${nonDeliverySectionClass}`}>
            <div className="flex items-center gap-2 pb-1 border-b border-gray-200">
              <FileText className="w-4 h-4 text-blue-600" />
              <h4 className="text-sm font-semibold text-gray-900">Notes &amp; Files</h4>
            </div>

            {/* Notes */}
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700">Notes</label>
              <textarea
                className={`w-full border border-gray-300 rounded-md px-3 py-2 text-sm resize-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  formDisabled || deliveryOnlyMode ? "bg-gray-50 text-gray-500 cursor-not-allowed" : "bg-white"
                }`}
                rows={3}
                value={form.notes}
                onChange={(e) => handleChange("notes", e.target.value)}
                placeholder="Special instructions, prep notes, or follow-up details..."
                disabled={formDisabled || deliveryOnlyMode}
              />
            </div>

            {/* Cancelation Notes - always editable when cancelled */}
            {showCancellationNotesField && (
              <div className="space-y-1">
                <label className="block text-sm font-medium text-red-700">Cancelation Notes</label>
                <textarea
                  className="w-full border border-red-300 rounded-md px-3 py-2 text-sm resize-none focus:ring-2 focus:ring-red-500 focus:border-red-500 bg-white"
                  rows={3}
                  value={form.cancellationNotes || ""}
                  onChange={(e) => handleChange("cancellationNotes", e.target.value)}
                  placeholder="Explain why this sample was cancelled..."
                />
              </div>
            )}

            {/* ✅ Comments (above attachments) */}
            <SampleOrderCommentsPanel
              companyId={companyId}
              sampleOrderId={request.id}
              currentUserId={currentUserId}
              currentUserName={
                currentUser?.fullName ||
                currentUser?.name ||
                currentUser?.displayName ||
                currentUser?.email ||
                request?.createdByName ||
                ""
              }
              currentUserRole={currentUser?.role || ""}
              companyUsers={companyUsers}
              isAdmin={isAdmin}
              onNotifyMentionedUsers={async ({
                companyId: notifCompanyId,
                sampleOrderId,
                commentId,
                commentText,
                commenterName,
                mentionedUserIds,
              }) => {
                await notifySampleCommentAdded({
                  companyId: notifCompanyId,
                  sampleRequestId: sampleOrderId,
                  sampleRequest: request,
                  commentId,
                  commentText,
                  commenterName,
                  mentionedUserIds,
                  companyUsers,
                  assignedToId: request?.assignedToId || request?.assignedUserId || request?.assignedTo || null,
                });
              }}
            />

            {/* ✅ Attachments BELOW comments */}
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700">Attachments</label>
              <AttachmentsPanel
  companyId={companyId}
  parentType="sampleRequest"
  parentId={request.id}
  currentUserId={currentUserId}

  // ✅ Claude "quick wins" metadata for better filtering/search later
  accountId={
    form.accountId === NEW_ACCOUNT_VALUE
      ? null
      : (form.accountId || request.accountId || null)
  }
  accountName={
    form.accountId === NEW_ACCOUNT_VALUE
      ? (newAccount.name?.trim() || null)
      : ((form.accountName || request.accountName || "").trim() || null)
  }

  // Admins can upload even if cancelled; non-admins view-only
  readOnly={!isAdmin || deliveryOnlyMode}
  title="Attachments"
  pageSize={50} // optional (matches the new pagination support)
/>

            </div>

          </div>

          {/* Meta & Actions */}
          <div className="mt-4 flex flex-col gap-2 border-t border-gray-200 bg-white px-4 py-3">
            <div className="flex items-center justify-between text-sm text-gray-500">
              <div className="flex items-center gap-1.5">
                <User className="w-4 h-4" />
                <span>
                  Created by{" "}
                  <span className="font-medium text-gray-700">
                    {request.createdByName || request.createdBy || "Unknown"}
                  </span>
                </span>
              </div>
              {createdAtDate && (
                <div className="flex items-center gap-1.5">
                  <CalendarIcon className="w-4 h-4" />
                  <span>
                    {createdAtDate.toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })}
                  </span>
                </div>
              )}
            </div>
          </div>

          <div
            className={
              inline
                ? "sticky bottom-0 z-10 -mx-4 mt-2 flex flex-row items-center justify-end gap-3 border-t border-slate-200 bg-slate-50/95 px-4 py-3 backdrop-blur"
                : embedded
                ? "sticky bottom-0 z-10 -mx-3 -mb-3 mt-2 flex flex-row items-center justify-end gap-3 border-t border-slate-200 bg-slate-50/95 px-3 py-3 backdrop-blur sm:-mx-4 sm:-mb-4 sm:px-4 sm:py-4"
                : "sticky bottom-0 z-10 -mx-3 -mb-3 mt-2 flex flex-row items-center justify-end gap-3 border-t border-slate-200 bg-slate-50/95 px-3 py-3 backdrop-blur sm:-mx-4 sm:-mb-4 sm:px-4 sm:py-4"
            }
          >
            <button
              type="button"
              onClick={onCancel}
              className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Send className="w-4 h-4" />
              {saving ? "Saving…" : "Save Changes"}
            </button>
          </div>
        </div>
        </form>
      </div>
    </div>
  );
};

export default EditSampleOrderForm;
